import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AffirmationsModule - Handles daily affirmation selection and display
 * 
 * @param {Object} props
 * @param {Object} props.selectedAffirmationData - Currently selected affirmation data
 * @param {Array} props.userTeams - User's team memberships for loading custom affirmations
 * @param {Function} props.onSelect - Callback when user selects an affirmation
 * @param {Function} props.onClear - Callback when user clears selected affirmation
 * @param {Function} props.setConfirmDialog - Function to show confirmation dialog
 */
const AffirmationsModule = ({ 
  selectedAffirmationData, 
  userTeams = [], 
  onSelect, 
  onClear,
  setConfirmDialog
}) => {
  const [affirmations, setAffirmations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAffirmations();
  }, [userTeams]);

  const loadAffirmations = async () => {
    setLoading(true);
    try {
      // Get default daily affirmations
      const dailyResponse = await axios.get(`${API}/affirmations/daily`);
      let allAffirmations = dailyResponse.data;
      
      
      // If user is on a team, also fetch custom team affirmations
      if (userTeams && userTeams.length > 0) {
        try {
          const teamResponse = await axios.get(`${API}/teams/${userTeams[0].id}/affirmations`);
          
          const customAffirmations = teamResponse.data.custom_affirmations || [];
          
          // Add custom affirmations to the list (they'll appear along with daily ones)
          if (customAffirmations.length > 0) {
            // Format custom affirmations to match the daily affirmations structure
            const formattedCustom = customAffirmations.map(aff => {
              return {
                id: aff.id,
                text: aff.text
              };
            });
            allAffirmations = [...formattedCustom, ...allAffirmations];
          }
        } catch (teamError) {
          console.error('Could not load team affirmations:', teamError);
          // Don't show error to user - just use daily affirmations
        }
      }
      
      setAffirmations(allAffirmations);
    } catch (error) {
      toast.error('Failed to load daily affirmations');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAI = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/affirmations/generate`);
      setAffirmations(response.data);
      toast.success('✨ AI-powered affirmations generated!');
    } catch (error) {
      console.error('Failed to generate AI affirmations:', error);
      toast.error('Failed to generate AI affirmations');
    } finally {
      setLoading(false);
    }
  };

  const handleClearClick = () => {
    setConfirmDialog({
      open: true,
      title: 'Clear Selected Affirmation?',
      description: 'This will remove your current affirmation selection and disable notifications.',
      confirmText: 'Clear',
      cancelText: 'Keep It',
      variant: 'default',
      onConfirm: onClear
    });
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="animate-pulse text-yellow-400">Loading affirmations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Always show instruction section at the top */}
      <div className="p-4 bg-sky-900/50 rounded-lg border border-sky-400/40">
        <h4 className="text-sky-400 font-semibold mb-2">💪 How to Use Affirmations</h4>
        <p className="text-gray-200 text-sm">
          Select a daily affirmation below. Once selected, take a moment to repeat it to yourself several times. 
          Feel its truth and let it resonate with your inner champion.
        </p>
      </div>

      {/* Show selected affirmation status */}
      {selectedAffirmationData && (
        <div className="p-4 bg-gradient-to-r from-blue-900 to-blue-800 rounded-lg border-l-4 border-yellow-500">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="text-white font-semibold mb-2">🌟 Your Selected Affirmation</h4>
              <p className="text-white font-medium text-lg">
                "{selectedAffirmationData.affirmation_text}"
              </p>
              <p className="text-sky-300 text-sm mt-2">
                Selected on {new Date(selectedAffirmationData.selected_at).toLocaleDateString()}
              </p>
              {selectedAffirmationData.notification_enabled && (
                <p className="text-sky-300 text-sm">
                  📱 Reminders active until 9 PM
                </p>
              )}
            </div>
            <Button
              onClick={handleClearClick}
              variant="outline"
              size="sm"
              className="text-sky-300 border-sky-400 hover:bg-blue-800"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Only show affirmation selection if no affirmation is selected */}
      {!selectedAffirmationData && (
        <>
          {/* AI Generation Toggle */}
          <div className="flex justify-center mb-4">
            <Button
              onClick={handleGenerateAI}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
              disabled={loading}
            >
              <span className="mr-2">✨</span>
              Generate AI-Powered Affirmations
            </Button>
          </div>
          
          <div className="space-y-3">
            {affirmations.map((affirmation) => {
              const isSelected = selectedAffirmationData?.affirmation_text === affirmation.text;
              return (
                <div 
                  key={affirmation.id}
                  className={`p-4 rounded-lg border-l-4 transition-all duration-200 ${
                    isSelected 
                      ? 'bg-gradient-to-r from-blue-900 to-blue-800 border-yellow-500 ring-2 ring-yellow-500/30' 
                      : 'bg-gradient-to-r from-blue-900 to-blue-800 border-sky-400 hover:border-yellow-400'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <p className="text-white font-medium text-lg flex-1 pr-4">
                      "{affirmation.text}"
                    </p>
                    <Button
                      onClick={() => onSelect(affirmation)}
                      size="sm"
                      className={`ml-2 transition-all duration-200 ${
                        isSelected 
                          ? 'bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold' 
                          : 'bg-yellow-500 hover:bg-yellow-600 text-slate-900'
                      }`}
                      disabled={loading || isSelected}
                    >
                      {isSelected ? (
                        <span className="flex items-center">
                          ✓ Selected
                        </span>
                      ) : (
                        'Select'
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default AffirmationsModule;
